/**
 * Workshop version of SearchFrame — Option B design.
 *
 * Props-based, no React Flow or store dependencies.
 * Header and search input are merged into one row — no separate title.
 * Result cards: favicon letter, domain, quoted snippet, optional thumbnail image.
 */

import { useState } from "react";
import { cn } from "@/lib/cn";
import { Icons } from "@/components/ui/icons";
import { Spinner } from "@/components/ui/spinner";
import { FrameFooter } from "@/workshop/components/shapes/shared/frame-footer";

// ─── Types ────────────────────────────────────────────────────────────────────

interface SearchResult {
  title: string;
  snippet: string;
  url: string;
  image?: string;
  isTopPick?: boolean;
}

interface SearchFrameProps {
  query: string;
  results: SearchResult[];
  status: "idle" | "streaming" | "error" | "done";
  agentName?: string;
  onCopy?: () => void;
  onSearch?: (query: string) => void;
  onResizeStart?: React.MouseEventHandler;
}

// ─── Favicon helpers ──────────────────────────────────────────────────────────

const FAVICON_COLORS = [
  "bg-blue-700",
  "bg-red-800",
  "bg-purple-700",
  "bg-emerald-700",
  "bg-orange-600",
  "bg-pink-700",
  "bg-teal-700",
  "bg-indigo-700",
];

function domainFromUrl(url: string): string {
  try {
    return new URL(url).hostname.replace("www.", "");
  } catch {
    return url;
  }
}

function faviconLetter(url: string): string {
  return domainFromUrl(url)[0]?.toUpperCase() ?? "?";
}

function faviconBgClass(url: string): string {
  const domain = domainFromUrl(url);
  let hash = 0;
  for (let i = 0; i < domain.length; i++) {
    hash = domain.charCodeAt(i) + ((hash << 5) - hash);
  }
  return FAVICON_COLORS[Math.abs(hash) % FAVICON_COLORS.length] ?? "";
}

// ─── Status color ─────────────────────────────────────────────────────────────

function statusDotColor(status: string): string {
  if (status === "streaming") return "bg-status-streaming";
  if (status === "error") return "bg-status-error";
  return "bg-muted";
}

// ─── Result card ──────────────────────────────────────────────────────────────

function ResultCard({ result, index }: { result: SearchResult; index: number }) {
  const domain = domainFromUrl(result.url);

  return (
    <div
      className={cn(
        "rounded-md p-2.5",
        result.isTopPick
          ? "bg-elevated/60 border border-accent/20"
          : "bg-elevated/40",
      )}
      data-testid={`search-result-${index}`}
    >
      <div className="flex gap-2">
        {/* Left: favicon + domain + title + quoted snippet */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-1.5">
            <div
              className={cn(
                "w-4 h-4 rounded-[3px] flex items-center justify-center shrink-0",
                faviconBgClass(result.url),
              )}
            >
              <span className="text-canvas-xs font-bold text-white leading-none">
                {faviconLetter(result.url)}
              </span>
            </div>
            <span className="text-canvas-base text-muted truncate">{domain}</span>
            {result.isTopPick && (
              <span className="ml-auto shrink-0 text-canvas-sm font-semibold bg-accent text-black px-1.5 py-0.5 rounded-[3px]">
                Top Pick
              </span>
            )}
          </div>

          <p className="text-canvas-base font-medium text-secondary mb-1 truncate">
            {result.title}
          </p>

          {result.snippet && (
            <p className="text-canvas-base leading-relaxed text-muted border-l-2 border-accent/40 pl-1.5 line-clamp-2">
              &ldquo;{result.snippet}&rdquo;
            </p>
          )}
        </div>

        {/* Right: website thumbnail or placeholder */}
        <div className="w-10 h-10 rounded overflow-hidden shrink-0 bg-elevated/60">
          {result.image && (
            <img
              src={result.image}
              alt={domain}
              className="w-full h-full object-cover"
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function SearchFrame({
  query: initialQuery,
  results,
  status,
  agentName,
  onCopy,
  onSearch,
  onResizeStart,
}: SearchFrameProps) {
  const [query, setQuery] = useState(initialQuery);

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter" && query.trim()) {
      onSearch?.(query.trim());
    }
  }

  return (
    <div
      className="denker-glass-frame flex flex-col rounded-frame overflow-hidden"
      data-testid="search-frame"
    >
      {/* Merged header + search input — single row */}
      <div
        className="flex h-8 w-full items-center gap-1.5 border-b border-glass-stroke-faint px-2"
        data-testid="search-frame-header"
      >
        <span className="h-3 w-0.5 shrink-0 rounded bg-frame-search" aria-hidden="true" />
        <Icons.Search className="h-2.5 w-2.5 shrink-0 text-muted" aria-hidden="true" />
        <input
          className="flex-1 min-w-0 bg-transparent text-canvas-base text-secondary placeholder:text-muted outline-none"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Search the web…"
          data-testid="search-frame-input"
        />
        {agentName && (
          <span className="shrink-0 text-canvas-2xs text-muted">{agentName}</span>
        )}
        <span
          className={cn("h-1.5 w-1.5 shrink-0 rounded-full", statusDotColor(status))}
          aria-hidden="true"
        />
      </div>

      {/* Results */}
      <div className="scrollbar-none flex-1 overflow-auto px-3 py-2">
        {status === "streaming" && results.length === 0 ? (
          <div className="flex items-center gap-2 py-3">
            <Spinner className="h-3 w-3" />
            <span className="text-canvas-base text-muted">Searching…</span>
          </div>
        ) : results.length > 0 ? (
          <div className="flex flex-col gap-2">
            {results.map((result, idx) => (
              <ResultCard key={result.url || idx} result={result} index={idx} />
            ))}
          </div>
        ) : (
          <p className="py-2 text-center text-canvas-base italic text-muted">
            No results
          </p>
        )}
      </div>

      <FrameFooter
        onCopy={onCopy}
        onResizeStart={onResizeStart}
        data-testid="search-frame-footer"
      />
    </div>
  );
}
